/**
 * bf26-landing.js
 *
 * Progressive enhancement for the Black Friday 2026 landing section (sections/bf26-landing.liquid).
 * Everything the page says is rendered server-side in Liquid; this file only adds behaviour:
 *
 *   1. Card galleries  [data-bf-gallery]: scroll-snap track, dots follow the scroll, a video slide
 *      plays (muted, looped) only while it is on screen.
 *   2. Filters         [data-bf-filters]: Riding / Budget / Top speed chips with live counts over the
 *      cards' data attributes. A chip with no result is disabled. One chip per group at a time.
 *   3. Signature Set   [data-bf-sig]: hotspot or tab zooms the base image onto the feature and fades
 *      the feature's own image in; prev / next step through the features and the overview. Cycles on its own while on screen until the
 *      visitor picks a feature, never under prefers-reduced-motion and never on a phone, where the
 *      controls are hidden and the visitor taps a feature instead.
 *   4. Video tiles     [data-bf-video]: a YouTube link opens an inline youtube-nocookie embed in a
 *      dialog; any other link (the channel fallback) is left to open normally.
 *   5. Draw countdown  [data-bf-timer]: days, hours, minutes and seconds to the weekly draw close
 *      (a weekday and time, in the section's time zone, Eastern by default), or to a fixed date and
 *      time in that zone when one is set.
 *
 * Without JavaScript the page still reads top to bottom: every card, the first slide of each
 * gallery, every feature tab and every video link work as plain markup.
 *
 * Re-runs on shopify:section:load so the theme editor shows live updates. Each root is guarded by
 * __bfInit so a second run never binds twice.
 */
(function (global) {
  'use strict';

  var EASE_MS = 1000;
  var SIG_INTERVAL = 4500;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  // The phone breakpoint, the same as the stylesheet's (max-width: 768px) block.
  var PHONE_QUERY = '(max-width: 768px)';

  /** The phone media query list, or null where matchMedia is missing. */
  function phoneQuery() {
    return typeof global.matchMedia === 'function' ? global.matchMedia(PHONE_QUERY) : null;
  }

  function onPhoneChange(list, fn) {
    if (typeof list.addEventListener === 'function') list.addEventListener('change', fn);
    else if (typeof list.addListener === 'function') list.addListener(fn);
  }

  function reducedMotion() {
    try {
      return typeof global.matchMedia === 'function' && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
      return false;
    }
  }

  /* ------------------------------------------------------------------ pure logic (tested) */

  /** The YouTube video id in a watch, share, shorts or embed URL; null for anything else (a channel). */
  function youtubeId(url) {
    if (!url || typeof url !== 'string') return null;
    var m = url.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  }

  /**
   * Which bucket of a filter group a card falls in, or null when it has no value for that group.
   * Budget: under the first step, from the first step to just under the second, the second step and
   * up (the chips read "Under $X", "$X to $Y", "$Y+"). Speed: whole numbers as the card shows them,
   * up to the first step, up to the second, above it.
   */
  function bucketOf(group, card, cfg) {
    if (group === 'ride') return card.ride || null;
    if (group === 'budget') {
      if (!(card.price >= 0)) return null;
      if (card.price < cfg.budgetLow) return 'low';
      if (card.price < cfg.budgetHigh) return 'mid';
      return 'high';
    }
    if (group === 'speed') {
      if (!(card.speed > 0)) return null;
      var speed = Math.round(card.speed);
      if (speed <= cfg.speedLow) return 'low';
      if (speed <= cfg.speedHigh) return 'mid';
      return 'high';
    }
    return null;
  }

  /** Whether a card passes every active filter, ignoring the group named in skip. */
  function passes(card, active, cfg, skip) {
    return Object.keys(active).every(function (group) {
      if (group === skip || active[group] == null) return true;
      return bucketOf(group, card, cfg) === active[group];
    });
  }

  /** How many cards a chip would show: the other groups' filters stay applied, its own is replaced. */
  function countFor(cards, active, cfg, group, value) {
    return cards.filter(function (card) {
      return passes(card, active, cfg, group) && bucketOf(group, card, cfg) === value;
    }).length;
  }

  /*
   * The zone the draw closes in: the section's (data-tz, an IANA name), or Eastern when it is empty
   * or not one this browser knows. Wall-clock fields come from Intl, so DST is the browser's.
   */
  var DRAW_TZ = 'America/New_York';
  var DAY_MS = 86400000;
  var wallFormats = {};

  /** The Intl formatter for zone tz, or false when this browser does not know the zone. */
  function wallFormat(tz) {
    if (!(tz in wallFormats)) {
      try {
        wallFormats[tz] = new Intl.DateTimeFormat('en-US', {
          timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric'
        });
      } catch (_) {
        wallFormats[tz] = false;
      }
    }
    return wallFormats[tz];
  }

  /** The zone to count in: tz when it is a zone this browser knows, Eastern otherwise. */
  function zoneOf(tz) {
    return tz && wallFormat(tz) ? tz : DRAW_TZ;
  }

  /** The wall-clock time in zone tz at instant ms, as if it were UTC (so Date.UTC arithmetic works). */
  function zoneWall(ms, tz) {
    var f = {};
    wallFormat(zoneOf(tz)).formatToParts(new Date(ms)).forEach(function (part) { f[part.type] = parseInt(part.value, 10); });
    return Date.UTC(f.year, f.month - 1, f.day, f.hour % 24, f.minute, f.second) + (ms % 1000 + 1000) % 1000;
  }

  /** The instant at which zone tz's clock reads wall (two passes, so a DST change in between is right). */
  function zoneToInstant(wall, tz) {
    var guess = wall - (zoneWall(wall, tz) - wall);
    return wall - (zoneWall(guess, tz) - guess);
  }

  /**
   * "23:59", "11:59 pm", "6 pm ET" or "18:00:00" as [hours, minutes]. A trailing name of the section's
   * own zone (ET for Eastern, AEDT or Sydney time for Sydney) is allowed; any other zone name makes the
   * text unreadable, so a time written for another zone is never read as this one. Anything
   * unreadable is 23:59.
   */
  var ZONE_NAMES = {
    'America/New_York': /\s*\(?\b(et|est|edt|eastern(\s+time)?)\b\)?\s*$/,
    'Australia/Sydney': /\s*\(?\b(aet|aest|aedt|sydney(\s+time)?)\b\)?\s*$/
  };

  function parseClock(text, tz) {
    var own = ZONE_NAMES[zoneOf(tz)];
    var t = String(text || '').trim().toLowerCase();
    if (own) t = t.replace(own, '');
    if (t === 'noon') return [12, 0];
    if (t === 'midnight') return [0, 0];
    var m = t.match(/^(\d{1,2})(?:[:.h](\d{2}))?(?::\d{2})?\s*(a\.?m\.?|p\.?m\.?)?$/);
    if (!m) return [23, 59];
    var h = parseInt(m[1], 10);
    var min = m[2] ? parseInt(m[2], 10) : 0;
    var half = m[3] ? m[3].charAt(0) : '';
    if (half && (h < 1 || h > 12)) return [23, 59];
    if (half === 'p' && h < 12) h += 12;
    if (half === 'a' && h === 12) h = 0;
    if (h > 23 || min > 59) return [23, 59];
    return [h, min];
  }

  /** A fixed close ("2026-11-29", optionally followed by a time) as epoch ms, or null when there is none. */
  function fixedClose(text, tz) {
    var end = String(text || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T,]+(.*))?$/);
    if (!end) return null;
    var hm = parseClock(end[4] || '23:59', tz);
    return zoneToInstant(Date.UTC(+end[1], end[2] - 1, +end[3], hm[0], hm[1], 59, 999), tz);
  }

  /**
   * When the draw closes, as epoch ms. cfg.end ("2026-11-29 23:59", in cfg.tz) wins when it reads as a
   * date; otherwise the next cfg.day (0 Sunday to 6 Saturday) at cfg.time, in cfg.tz, after now. The
   * close is the end of that minute, as "11:59 pm" means. cfg.tz is Eastern when empty or unknown.
   */
  function drawClose(now, cfg) {
    var fixed = fixedClose(cfg.end, cfg.tz);
    if (fixed !== null) return fixed;
    var day = parseInt(cfg.day, 10);
    if (!(day >= 0 && day <= 6)) day = 0;
    var clock = parseClock(cfg.time, cfg.tz);
    var today = new Date(zoneWall(now, cfg.tz));
    var wall = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + (day - today.getUTCDay() + 7) % 7, clock[0], clock[1], 59, 999);
    var at = zoneToInstant(wall, cfg.tz);
    if (at <= now) at = zoneToInstant(wall + 7 * DAY_MS, cfg.tz);
    return at;
  }

  /** Time left as whole days, hours, minutes and seconds; zero once it has passed. */
  function splitLeft(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    return { d: Math.floor(s / 86400), h: Math.floor(s / 3600) % 24, m: Math.floor(s / 60) % 60, s: s % 60 };
  }

  /* ------------------------------------------------------------------ 1. galleries */

  function initGallery(root) {
    if (root.__bfInit) return;
    root.__bfInit = true;
    var track = root.querySelector('[data-bf-track]');
    var dots = toArray(root.querySelectorAll('[data-bf-dot]'));
    if (!track) return;
    var slides = toArray(track.children);

    function current() {
      var w = track.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w)));
    }

    function sync() {
      var i = current();
      dots.forEach(function (dot, n) {
        dot.classList.toggle('is-active', n === i);
        dot.setAttribute('aria-current', n === i ? 'true' : 'false');
      });
    }

    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = global.requestAnimationFrame(function () {
        raf = 0;
        sync();
      });
    }, { passive: true });

    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () {
        track.scrollTo({ left: n * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      });
    });

    var videos = toArray(track.querySelectorAll('video'));
    if (videos.length && 'IntersectionObserver' in global) {
      var io = new global.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6 && !reducedMotion()) {
            var p = v.play();
            if (p && typeof p.catch === 'function') p.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: [0, 0.6, 1] });
      videos.forEach(function (v) {
        io.observe(v);
      });
    }
    sync();
  }

  /* ------------------------------------------------------------------ 2. filters */

  function readCard(el) {
    return {
      el: el,
      ride: el.getAttribute('data-ride') || null,
      price: parseInt(el.getAttribute('data-price'), 10),
      speed: parseFloat(el.getAttribute('data-speed'))
    };
  }

  function initFilters(section) {
    var root = section.querySelector('[data-bf-filters]');
    if (!root || root.__bfInit) return;
    root.__bfInit = true;
    var cfg = {
      budgetLow: parseInt(root.getAttribute('data-budget-low'), 10),
      budgetHigh: parseInt(root.getAttribute('data-budget-high'), 10),
      speedLow: parseFloat(root.getAttribute('data-speed-low')),
      speedHigh: parseFloat(root.getAttribute('data-speed-high'))
    };
    var cards = toArray(section.querySelectorAll('[data-bf-card]')).map(readCard);
    var chips = toArray(root.querySelectorAll('[data-bf-chip]'));
    var reset = root.querySelector('[data-bf-reset]');
    var empty = section.querySelector('[data-bf-empty]');
    var active = { ride: null, budget: null, speed: null };

    function render() {
      cards.forEach(function (card) {
        card.el.hidden = !passes(card, active, cfg, null);
      });
      chips.forEach(function (chip) {
        var group = chip.getAttribute('data-group');
        var value = chip.getAttribute('data-value');
        var on = active[group] === value;
        var count = countFor(cards, active, cfg, group, value);
        var countEl = chip.querySelector('[data-bf-count]');
        if (countEl) countEl.textContent = String(count);
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        chip.disabled = count === 0 && !on;
      });
      var any = Object.keys(active).some(function (k) {
        return active[k] != null;
      });
      if (reset) reset.hidden = !any;
      if (empty) {
        empty.hidden = cards.some(function (card) {
          return !card.el.hidden;
        });
      }
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var group = chip.getAttribute('data-group');
        var value = chip.getAttribute('data-value');
        active[group] = active[group] === value ? null : value;
        render();
      });
    });
    function clear() {
      active = { ride: null, budget: null, speed: null };
      render();
    }
    if (reset) reset.addEventListener('click', clear);

    // Phones hide the filter bar; a filter picked on a wider screen must not keep cards hidden there.
    var phoneList = phoneQuery();
    if (phoneList) {
      onPhoneChange(phoneList, function () {
        if (phoneList.matches) clear();
      });
    }
    render();
  }

  /* ------------------------------------------------------------------ 3. Signature Set */

  function initSignature(root) {
    if (root.__bfInit) return;
    root.__bfInit = true;
    var base = root.querySelector('[data-bf-sig-base]');
    var layers = toArray(root.querySelectorAll('[data-bf-sig-layer]'));
    var pickers = toArray(root.querySelectorAll('[data-bf-sig-pick]'));
    var caption = root.querySelector('[data-bf-sig-caption]');
    var capName = root.querySelector('[data-bf-sig-name]');
    var capDesc = root.querySelector('[data-bf-sig-desc]');
    var capCount = root.querySelector('[data-bf-sig-count]');
    function coord(value) {
      var n = parseFloat(value);
      return isNaN(n) ? 50 : n;
    }
    var items = toArray(root.querySelectorAll('[data-bf-sig-item]')).map(function (el) {
      return {
        name: el.getAttribute('data-name') || '',
        desc: el.getAttribute('data-desc') || '',
        x: coord(el.getAttribute('data-x')),
        y: coord(el.getAttribute('data-y'))
      };
    });
    var n = items.length;
    if (!n) return;
    // The feature shown first (data-start, 1-based; 0 is the overview). Without an overview image the
    // round skips the empty stage, unless the page was set to open on it.
    var hasBase = !root.classList.contains('bf26-sig--no-base');
    var first = parseInt(root.getAttribute('data-start'), 10) - 1;
    if (!(first >= -1 && first < n)) first = -1;
    var sel = first;
    var auto = !reducedMotion();
    // Phones hide the slideshow's controls, so it waits there (the visitor taps a feature) and
    // carries on if the screen grows past the breakpoint again.
    var phoneList = phoneQuery();
    function onPhone() {
      return !!(phoneList && phoneList.matches);
    }
    var timer = 0;
    var visible = false;

    function show(i) {
      sel = i;
      root.classList.toggle('is-zoomed', i >= 0);
      if (i >= 0 && base) {
        // The CSS turns these into the zoom point, following the picture's crop on wide screens.
        base.style.setProperty('--bf-sig-ox', items[i].x);
        base.style.setProperty('--bf-sig-oy', items[i].y);
      }
      layers.forEach(function (layer) {
        layer.classList.toggle('is-active', parseInt(layer.getAttribute('data-index'), 10) === i);
      });
      pickers.forEach(function (p) {
        var on = parseInt(p.getAttribute('data-index'), 10) === i;
        p.classList.toggle('is-active', on);
        if (p.getAttribute('role') === 'tab') p.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (caption) caption.hidden = i < 0;
      if (i >= 0) {
        if (capName) capName.textContent = items[i].name;
        if (capDesc) capDesc.textContent = items[i].desc;
        if (capCount) capCount.textContent = (i + 1 < 10 ? '0' : '') + (i + 1) + ' / ' + (n < 10 ? '0' : '') + n;
      }
    }

    var pause = root.querySelector('[data-bf-sig-pause]');

    // The slideshow stops for good on any choice, on the pause button, or after one round. While it
    // runs the caption is not announced; once the visitor drives it, it is.
    function halt() {
      auto = false;
      stop();
      if (caption) caption.setAttribute('aria-live', 'polite');
      if (pause) {
        pause.setAttribute('aria-pressed', 'true');
        pause.hidden = true;
      }
    }

    function pick(i) {
      halt();
      show(i);
    }

    function stop() {
      if (timer) global.clearInterval(timer);
      timer = 0;
    }

    // One round: every feature (and the overview, when there is one) once, ending where it started.
    // Prev and next walk the same ring, so the overview is always one step away.
    function following(i) {
      if (i >= n - 1) return hasBase || first === -1 ? -1 : 0;
      return i + 1;
    }

    function preceding(i) {
      if (i === 0 && (hasBase || first === -1)) return -1;
      return i <= 0 ? n - 1 : i - 1;
    }

    function start() {
      if (!auto || timer || !visible || onPhone()) return;
      timer = global.setInterval(function () {
        var to = following(sel);
        show(to);
        if (to === first) halt();
      }, SIG_INTERVAL);
    }

    if (pause) {
      if (!auto) pause.hidden = true;
      pause.addEventListener('click', halt);
    }
    if (phoneList) {
      onPhoneChange(phoneList, function () {
        if (phoneList.matches) stop();
        else start();
      });
    }

    pickers.forEach(function (p) {
      p.addEventListener('click', function () {
        pick(parseInt(p.getAttribute('data-index'), 10));
      });
    });
    var prev = root.querySelector('[data-bf-sig-prev]');
    var next = root.querySelector('[data-bf-sig-next]');
    if (prev) prev.addEventListener('click', function () { pick(preceding(sel)); });
    if (next) next.addEventListener('click', function () { pick(following(sel)); });

    if ('IntersectionObserver' in global) {
      new global.IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) start();
        else stop();
      }, { threshold: 0.35 }).observe(root);
    }
    show(first);
    root.style.setProperty('--bf-sig-ease', EASE_MS + 'ms');
  }

  /* ------------------------------------------------------------------ 5. draw countdown */

  function initTimer(root) {
    if (root.__bfInit) return;
    root.__bfInit = true;
    var cfg = { day: root.getAttribute('data-day'), time: root.getAttribute('data-time'), end: root.getAttribute('data-end'), tz: root.getAttribute('data-tz') };
    var cells = {};
    ['d', 'h', 'm', 's'].forEach(function (k) { cells[k] = root.querySelector('[data-bf-timer-' + k + ']'); });
    var fixed = fixedClose(cfg.end, cfg.tz) !== null;
    var close = 0;
    var tick = 0;

    function pad(v) {
      return v < 10 ? '0' + v : String(v);
    }

    function paint() {
      // The section was replaced (the theme editor reloads it) or is hidden (phones): stop, or skip.
      if (!root.isConnected) {
        global.clearInterval(tick);
        tick = 0;
        return;
      }
      if (close && root.offsetParent === null) return;
      var now = Date.now();
      // A weekly close rolls to the next week once passed; a fixed one stays at zero.
      if (!close || (now > close && !fixed)) close = drawClose(now, cfg);
      var left = splitLeft(close - now);
      Object.keys(cells).forEach(function (k) {
        if (cells[k]) cells[k].textContent = pad(left[k]);
      });
      if (close <= now && fixed && tick) {
        global.clearInterval(tick);
        tick = 0;
      }
    }

    try {
      paint();
    } catch (_) {
      return;
    }
    root.classList.add('is-live');
    tick = global.setInterval(paint, 1000);
  }

  /* ------------------------------------------------------------------ 4. video dialog */

  function initVideos(section) {
    var dialog = section.querySelector('[data-bf-dialog]');
    var frameWrap = dialog && dialog.querySelector('[data-bf-dialog-frame]');
    if (!dialog || !frameWrap || dialog.__bfInit || typeof dialog.showModal !== 'function') return;
    dialog.__bfInit = true;
    var opener = null;

    function close() {
      frameWrap.innerHTML = '';
      if (dialog.open) dialog.close();
      if (opener && typeof opener.focus === 'function') opener.focus();
    }

    toArray(section.querySelectorAll('[data-bf-video]')).forEach(function (link) {
      link.addEventListener('click', function (event) {
        var id = youtubeId(link.getAttribute('href'));
        if (!id) return;
        event.preventDefault();
        opener = link;
        var iframe = global.document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1';
        iframe.title = link.getAttribute('data-title') || 'Apollo owner review';
        iframe.loading = 'lazy';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        frameWrap.innerHTML = '';
        frameWrap.appendChild(iframe);
        dialog.showModal();
      });
    });
    toArray(dialog.querySelectorAll('[data-bf-dialog-close]')).forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', function () {
      frameWrap.innerHTML = '';
    });
  }

  /* ------------------------------------------------------------------ boot */

  function init(scope) {
    var doc = scope || global.document;
    toArray(doc.querySelectorAll('[data-bf26]')).forEach(function (section) {
      toArray(section.querySelectorAll('[data-bf-gallery]')).forEach(initGallery);
      initFilters(section);
      toArray(section.querySelectorAll('[data-bf-sig]')).forEach(initSignature);
      initVideos(section);
      toArray(section.querySelectorAll('[data-bf-timer]')).forEach(initTimer);
    });
  }

  global.Bf26Landing = {
    init: init,
    youtubeId: youtubeId,
    bucketOf: bucketOf,
    passes: passes,
    countFor: countFor,
    drawClose: drawClose,
    splitLeft: splitLeft
  };

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () { init(); });
    } else {
      init();
    }
    global.document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})(typeof window !== 'undefined' ? window : this);
